import { useId, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Inbox, Loader2 } from 'lucide-react'
import { cn } from '../lib/utils'

/*
 * Dashboard building blocks for AFOS 360 (AFOS-41).
 *
 * Built to the data-viz method: forms chosen by the data's job (KPI row of stat
 * tiles, ranked horizontal bars for counts by category, a meter for a ratio
 * against a limit); one hue per chart; text in foam/mist ink, never the mark
 * color; thin marks with 4px rounded data ends anchored to the baseline and a
 * 2px gap between bars; a hover tooltip on every bar; a table view for every
 * chart. Mark hues are the dark-mode steps validated against the panel surface
 * (--chart-cyan #1aa891, --chart-amber #c98228; both pass lightness, chroma,
 * CVD, and 3:1 contrast on #0b1224).
 */

export function StatTile({
  label,
  value,
  hint,
  icon,
  emphasis = false,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  icon?: ReactNode
  emphasis?: boolean
}) {
  return (
    <div className={cn('glass rounded-3xl p-5', emphasis && 'neon-ring')}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-mist)]">
          {label}
        </span>
        {icon}
      </div>
      <div
        className={cn(
          'mt-3 font-[family-name:var(--font-display)] font-extrabold tracking-tight text-[var(--color-foam)]',
          emphasis ? 'text-5xl' : 'text-4xl',
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-2 text-sm text-[var(--color-mist)]">{hint}</div>}
    </div>
  )
}

export type BarDatum = { key: string; label: string; value: number; href?: string }

/** Ranked horizontal bars, one hue, largest first, values labelled directly. */
export function RankedBars({
  title,
  data,
  tone = 'cyan',
  unit = '',
  empty,
}: {
  title: string
  data: BarDatum[]
  tone?: 'cyan' | 'amber'
  unit?: string
  empty: ReactNode
}) {
  const [showTable, setShowTable] = useState(false)
  const [hover, setHover] = useState<string | null>(null)
  const tableId = useId()
  const rows = [...data].sort((a, b) => b.value - a.value)
  const max = Math.max(1, ...rows.map((r) => r.value))
  const total = rows.reduce((sum, r) => sum + r.value, 0)
  const fill = tone === 'amber' ? 'var(--chart-amber)' : 'var(--chart-cyan)'

  return (
    <figure className="glass rounded-3xl p-5">
      <figcaption className="mb-4 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-[var(--color-foam)]">{title}</span>
        {rows.length > 0 && (
          <button
            type="button"
            aria-controls={tableId}
            aria-expanded={showTable}
            onClick={() => setShowTable((v) => !v)}
            className="rounded-full border border-[var(--color-line)] px-3 py-1 text-xs text-[var(--color-mist)] hover:text-[var(--color-foam)]"
          >
            {showTable ? 'Chart view' : 'Table view'}
          </button>
        )}
      </figcaption>
      {rows.length === 0 ? (
        <EmptyState compact>{empty}</EmptyState>
      ) : showTable ? (
        <table id={tableId} className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[var(--color-mist)]">
              <th className="pb-2 font-semibold">Category</th>
              <th className="pb-2 text-right font-semibold">Count</th>
              <th className="pb-2 text-right font-semibold">Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-[var(--color-line)]">
                <td className="py-2 text-[var(--color-foam)]">{r.label}</td>
                <td className="py-2 text-right tabular-nums text-[var(--color-foam)]">
                  {r.value}
                  {unit}
                </td>
                <td className="py-2 text-right tabular-nums text-[var(--color-mist)]">
                  {total ? Math.round((r.value / total) * 100) : 0}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <ul className="space-y-[2px]" aria-label={title}>
          {rows.map((r) => {
            const pct = (r.value / max) * 100
            const share = total ? Math.round((r.value / total) * 100) : 0
            const body = (
              <div className="group relative py-1.5">
                <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                  <span className="truncate text-[var(--color-foam)]">{r.label}</span>
                  <span className="tabular-nums text-[var(--color-mist)]">
                    {r.value}
                    {unit}
                  </span>
                </div>
                <div className="h-2 w-full rounded-[4px] bg-[var(--chart-track)]">
                  <div
                    className="h-2 rounded-r-[4px] transition-[width] duration-500"
                    style={{ width: `${Math.max(pct, r.value > 0 ? 2 : 0)}%`, background: fill }}
                  />
                </div>
                {hover === r.key && (
                  <div
                    role="tooltip"
                    className="pointer-events-none absolute right-0 top-0 z-20 -translate-y-full rounded-xl border border-[var(--color-line)] bg-[var(--color-ink)] px-3 py-2 text-xs text-[var(--color-foam)] shadow-xl"
                  >
                    <div className="font-semibold">{r.label}</div>
                    <div className="text-[var(--color-mist)]">
                      {r.value}
                      {unit} · {share}% of {total}
                    </div>
                  </div>
                )}
              </div>
            )
            return (
              <li
                key={r.key}
                onMouseEnter={() => setHover(r.key)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(r.key)}
                onBlur={() => setHover(null)}
              >
                {r.href ? (
                  <Link to={r.href} className="block rounded-xl px-1 hover:bg-white/5">
                    {body}
                  </Link>
                ) : (
                  <div className="px-1">{body}</div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </figure>
  )
}

/** A single ratio against a limit, on a same-ramp track. */
export function Meter({
  label,
  used,
  limit,
  hint,
}: {
  label: string
  /** null = usage could not be established (never shown as zero). */
  used: number | null
  limit?: number | null
  hint?: ReactNode
}) {
  if (used === null) {
    return (
      <div className="glass rounded-3xl p-5">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-semibold text-[var(--color-foam)]">{label}</span>
          <span className="text-sm text-[var(--color-mist)]">Unavailable</span>
        </div>
        <div className="mt-3 h-2 w-full rounded-[4px] border border-dashed border-[var(--color-line)]" aria-hidden="true" />
        <div className="mt-2 text-xs text-[var(--color-mist)]">
          {hint ?? 'Usage could not be read; it is not shown as zero.'}
        </div>
      </div>
    )
  }
  const known = typeof limit === 'number' && limit > 0
  const pct = known ? Math.min(100, (used / (limit as number)) * 100) : 0
  const near = known && pct >= 80
  return (
    <div className="glass rounded-3xl p-5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold text-[var(--color-foam)]">{label}</span>
        <span className="tabular-nums text-sm text-[var(--color-mist)]">
          {used}
          {known ? ` / ${limit}` : ' · no limit set'}
        </span>
      </div>
      <div
        className="mt-3 h-2 w-full rounded-[4px] bg-[var(--chart-track)]"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={known ? (limit as number) : undefined}
        aria-valuenow={used}
      >
        <div
          className="h-2 rounded-r-[4px]"
          style={{
            width: `${known ? Math.max(pct, used > 0 ? 2 : 0) : 0}%`,
            background: near ? 'var(--chart-amber)' : 'var(--chart-cyan)',
          }}
        />
      </div>
      {(hint || near) && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-[var(--color-mist)]">
          {near && <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 text-[var(--color-amber)]" />}
          {near ? <span>Within 20% of the limit. </span> : null}
          {hint}
        </div>
      )}
    </div>
  )
}

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-3 rounded-3xl border border-[var(--color-line)] bg-black/20 px-5 py-6 text-sm text-[var(--color-mist)]"
    >
      <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin text-[var(--color-cyan)]" />
      {label}…
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-[rgba(255,107,138,0.4)] bg-[rgba(255,107,138,0.08)] px-5 py-4 text-sm text-[var(--color-foam)]"
    >
      <span className="flex items-center gap-2">
        <AlertTriangle aria-hidden="true" className="h-4 w-4 text-[var(--color-rose)]" />
        {message}
      </span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-full border border-[var(--color-line)] px-3 py-1.5 text-xs text-[var(--color-foam)] hover:bg-white/5"
        >
          Try again
        </button>
      )}
    </div>
  )
}

export function EmptyState({ children, compact = false }: { children: ReactNode; compact?: boolean }) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-2xl border border-dashed border-[var(--color-line)] text-sm text-[var(--color-mist)]',
        compact ? 'px-4 py-4' : 'px-5 py-8',
      )}
    >
      <Inbox aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
      <div>{children}</div>
    </div>
  )
}

/** Names sources the server could not read, so a blank is never mistaken for zero. */
export function UnavailableNotice({ sources }: { sources: Record<string, string> }) {
  const keys = Object.keys(sources)
  if (keys.length === 0) return null
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-2xl border border-[rgba(255,179,71,0.35)] bg-[rgba(255,179,71,0.08)] px-4 py-3 text-sm text-[var(--color-foam)]"
    >
      <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-amber)]" />
      <span>
        Some sources could not be read and are shown as unavailable rather than zero:{' '}
        {keys.map((k) => `${k} (${sources[k]})`).join(', ')}.
      </span>
    </div>
  )
}

/** Coverage and access notes: partial scans and data hidden by role. */
export function CoverageNotice({
  incomplete,
  restricted,
}: {
  incomplete?: string[]
  restricted?: Record<string, string>
}) {
  const parts: string[] = []
  if (incomplete && incomplete.length) {
    parts.push(`Counts for ${incomplete.join(', ')} cover the newest records only (scan ceiling reached).`)
  }
  const hidden = Object.keys(restricted ?? {})
  if (hidden.length) {
    parts.push(`${hidden.join(', ')} hidden: your role does not include support access.`)
  }
  if (!parts.length) return null
  return (
    <div role="status" className="rounded-2xl border border-[var(--color-line)] bg-black/25 px-4 py-3 text-sm text-[var(--color-mist)]">
      {parts.join(' ')}
    </div>
  )
}

/** Human labels for stored state keys. */
export function humanize(key: string): string {
  const text = key.replace(/^outcome_/, 'outcome: ').replace(/_/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}
